import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Circle, Clock, Mail, Phone, DollarSign, Sparkles, Calendar, Package } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import { api, formatApiError } from "../lib/api";

// Module-level animation constants to prevent object recreation on each render
const CARD_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const MILESTONE_ANIM = (i) => ({ initial: { opacity: 0, x: -16 }, animate: { opacity: 1, x: 0 }, transition: { delay: i * 0.08 } });

const STATUS_COLORS = {
  new: "bg-[#00D4FF]/15 text-[#00D4FF] border-[#00D4FF]/30",
  reviewing: "bg-violet-400/15 text-violet-300 border-violet-400/30",
  in_progress: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
  completed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  rejected: "bg-red-400/15 text-red-300 border-red-400/30",
};

export default function TrackProject() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // quoteId is the real dependency; api/formatApiError are stable module-level refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${quoteId}/tracking`);
        setData(data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      }
      setLoading(false);
    })();
  }, [quoteId]);

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Link to="/dashboard" data-testid="track-back-btn" className="inline-flex items-center gap-2 text-sm text-[#5A5A72] hover:text-[#00D4FF] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>

          {loading ? (
            <Loader label="SYNCING PROJECT DATA..." />
          ) : error ? (
            <div className="glass-card rounded-2xl p-8 border border-red-500/20" data-testid="track-error">
              <p className="text-red-400">{error}</p>
            </div>
          ) : data ? (
            <>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ project tracking</p>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight">
                  {data.quote.project_title}
                </h1>
                <span data-testid="track-status-badge" className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${STATUS_COLORS[data.quote.status] || STATUS_COLORS.new}`}>
                  {data.quote.status.replace("_", " ")}
                </span>
              </div>
              <p className="text-[#00D4FF] font-mono text-sm mt-2">{data.quote.service_type}</p>

              {/* Progress bar + expected delivery */}
              <div className="mt-10 glass-card rounded-2xl p-7">
                <div className="flex items-end justify-between mb-3">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#5A5A72]">Project Progress</p>
                  <p data-testid="track-progress-value" className="font-display font-black text-3xl text-gradient-cyan">{data.progress}%</p>
                </div>
                <div className="w-full h-2.5 bg-[#F2F2F5] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }} animate={{ width: `${data.progress}%` }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full bg-gradient-to-r from-[#00D4FF] via-[#2060EE] to-[#8B2FE8]"
                  />
                </div>

                {/* Delivery timeline strip */}
                <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="glass-card rounded-xl p-3 border border-[rgba(14,14,26,0.06)]">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A5A72] flex items-center gap-1"><Calendar className="w-3 h-3" />Started</p>
                    <p className="font-semibold text-[#0E0E1A] mt-1 text-sm">{new Date(data.quote.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}</p>
                  </div>
                  <div className="glass-card rounded-xl p-3 border border-[rgba(14,14,26,0.06)]">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A5A72] flex items-center gap-1"><Package className="w-3 h-3" />Expected Delivery</p>
                    {data.quote.expected_delivery ? (
                      <p data-testid="track-delivery-date" className="font-semibold text-[#2060EE] mt-1 text-sm">
                        {new Date(data.quote.expected_delivery).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                      </p>
                    ) : (
                      <p className="text-[#9191A8] text-sm mt-1 italic">Being confirmed</p>
                    )}
                  </div>
                  {data.quote.timeline && (
                    <div className="glass-card rounded-xl p-3 border border-[rgba(14,14,26,0.06)]">
                      <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A5A72] flex items-center gap-1"><Clock className="w-3 h-3" />Your Timeline</p>
                      <p className="font-semibold text-[#0E0E1A] mt-1 text-sm">{data.quote.timeline}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Brief */}
              <div className="mt-6 glass-card rounded-2xl p-7">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-3">/ brief</p>
                <p className="text-[#374151] leading-relaxed whitespace-pre-wrap">{data.quote.description}</p>
              </div>

              {/* Timeline */}
              <div className="mt-6 glass-card rounded-2xl p-7">
                <div className="flex items-center justify-between mb-6">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00D4FF]">/ delivery timeline</p>
                  <span className="text-xs text-[#6B7280] font-mono">{data.milestones.length} updates</span>
                </div>

                {data.milestones.length === 0 ? (
                  <div className="text-center py-10" data-testid="track-no-milestones">
                    <div className="w-12 h-12 rounded-2xl mx-auto mb-3 bg-[#00D4FF]/10 grid place-items-center border border-[#00D4FF]/20">
                      <Sparkles className="w-5 h-5 text-[#00D4FF]" />
                    </div>
                    <p className="text-[#4B5563]">Vedant hasn't posted a milestone yet.</p>
                    <p className="text-[#6B7280] text-sm mt-1">You'll see progress updates here as your project moves forward.</p>
                  </div>
                ) : (
                  <ol className="relative border-l border-[#00D4FF]/20 ml-3 space-y-7">
                    {data.milestones.map((m, i) => (
                      <motion.li
                        key={m.id}
                        initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.06 }}
                        data-testid={`milestone-${m.id}`}
                        className="pl-6 relative"
                      >
                        <span className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 ${m.percent === 100 ? "bg-emerald-400 border-emerald-400" : "bg-[#00D4FF] border-[#00D4FF] glow-cyan"}`} />
                        <div className="flex flex-wrap items-baseline gap-3">
                          <h3 className="font-display font-bold text-lg">{m.title}</h3>
                          <span className="text-xs font-mono text-[#00D4FF]">{m.percent}%</span>
                          <span className="text-xs font-mono text-[#6B7280] ml-auto">
                            {new Date(m.created_at).toLocaleString()}
                          </span>
                        </div>
                        {m.note && <p className="text-[#4B5563] text-sm mt-2 leading-relaxed">{m.note}</p>}
                      </motion.li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Contact */}
              <div className="mt-6 glass-card rounded-2xl p-7">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-4">/ project owner</p>
                <div className="grid sm:grid-cols-2 gap-4 text-sm text-[#4B5563]">
                  <div>
                    <p className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Lead</p>
                    <p>Vedant Baligar — Founder & Operator, Xion</p>
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Contact on file</p>
                    <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#00D4FF]" />{data.quote.contact_email}</p>
                    {data.quote.contact_phone && (
                      <p className="flex items-center gap-2 mt-1"><Phone className="w-3.5 h-3.5 text-[#00D4FF]" />{data.quote.contact_phone}</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </section>
      <Footer />
    </div>
  );
}
